import React from "react";
import { FaRegCircleQuestion } from "react-icons/fa6";
import { twMerge } from "tailwind-merge";
import type { LgvRadioOption } from "../utils/radioOptions";
import { valuesMatchRadio } from "../utils/radioOptions";

export interface LgvRadioGroupProps {
  name: string;
  value: unknown;
  options: LgvRadioOption[];
  onChange: (value: string | number | boolean) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  onOptionHintClick?: (
    hint: { title?: string; content: string },
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}

const LgvRadioGroup: React.FC<LgvRadioGroupProps> = ({
  name,
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  className,
  onOptionHintClick,
}) => {
  return (
    <div
      role="radiogroup"
      aria-required={required || undefined}
      className={twMerge("space-y-2", className)}
    >
      {options.map((opt, index) => {
        const optionId = `${name}-${index}`;
        const isSelected = valuesMatchRadio(value, opt.value);

        return (
          <label
            key={optionId}
            htmlFor={optionId}
            className={twMerge(
              "flex w-full cursor-pointer items-center rounded-lg border px-3 py-2.5 transition-colors",
              isSelected
                ? "border-[#1B365D]/25 bg-[#EFF6FF] hover:bg-[#E5EFFF]"
                : [
                    "border-[#1B365D]/15 bg-transparent",
                    "hover:border-[#1B365D]/20 hover:bg-slate-50",
                    "focus-within:ring-2 focus-within:ring-[#1B365D]/15 focus-within:ring-offset-1",
                  ].join(" "),
              disabled && "cursor-not-allowed opacity-50 hover:border-[#1B365D]/15 hover:bg-transparent",
            )}
          >
            <input
              type="radio"
              id={optionId}
              name={name}
              checked={isSelected}
              disabled={disabled}
              required={required && index === 0}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            <div className="flex min-w-0 items-center gap-2">
              <span
                className={twMerge(
                  "text-base leading-snug text-[#1B365D]",
                  isSelected && "font-medium",
                )}
              >
                {opt.label}
              </span>
              {opt.explanation && onOptionHintClick && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-[#1B365D] transition-colors"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onOptionHintClick(
                      {
                        title: opt.label,
                        content: opt.explanation ?? "",
                      },
                      event,
                    );
                  }}
                  aria-label="Show option hint in sidebar"
                >
                  <FaRegCircleQuestion className="text-sm" />
                </button>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
};

export default LgvRadioGroup;
